import type { ChartPointId, Planet, WesternAspectType } from '@astrally/shared';
import { CHARA_KARANA_NAMES, YOGA_NAMES } from '../jyotish/panchanga.js';

/**
 * The interpretive vocabulary of the horoscope engine, expressed as DATA.
 *
 * Nothing in this file computes anything. It is the lookup layer that turns an
 * engine-computed configuration ("transiting Saturn is 1°12' from a square to
 * natal Venus", "the Moon is in the 12th from your natal Moon") into a short
 * label and a one-line meaning a 19-year-old with zero astrology background can
 * read.
 *
 * Copy rules (docs/PRODUCT_COPY.md; enforced by review, not by types):
 *  - Say what a placement MEANS inside the system, never what WILL happen.
 *    "worth asking where…", "this is the part of the week where…" — never "you will…".
 *    The word "lens" is BANNED from output copy: it is workshop vocabulary,
 *    and it reached the daily headline once. Plain words only.
 *  - Explain the jargon inline, or hang a glossary `term` off the theme so the
 *    UI can open the explanation (ids live in app/src/features/glossary/terms.ts).
 *  - Short label, one-line meaning. The AI layer rephrases these; it must never
 *    need to invent substance that is not already here.
 */

// ---------------------------------------------------------------------------
// Life areas
// ---------------------------------------------------------------------------

export type LifeArea =
  | 'career'
  | 'money'
  | 'relationships'
  | 'health'
  | 'learning'
  | 'home'
  | 'travel'
  | 'inner';

/** Stable display order. Also the key order of `byArea` in a horoscope result. */
export const LIFE_AREAS: LifeArea[] = [
  'career',
  'money',
  'relationships',
  'health',
  'learning',
  'home',
  'travel',
  'inner',
];

export const LIFE_AREA_LABELS: Record<LifeArea, string> = {
  career: 'Work & ambition',
  money: 'Money & resources',
  relationships: 'People & closeness',
  health: 'Body & energy',
  learning: 'Learning & ideas',
  home: 'Home & family',
  travel: 'Travel & horizons',
  inner: 'Inner life',
};

export interface Theme {
  /** Short and punchy — reads as a chip or a card heading. */
  label: string;
  /** One line of plain English. What it MEANS, never what will happen. */
  meaning: string;
  area: LifeArea;
  /** Base significance, 0..1, before orb / speed / period weighting. */
  weight: number;
  /** Glossary term id, when one explains the jargon in the label. */
  term?: string;
}

// ---------------------------------------------------------------------------
// Planet voices — what each body is "about"
// ---------------------------------------------------------------------------

export interface PlanetVoice {
  /** One-word handle. */
  keyword: string;
  /** Noun phrase used when this body is the one moving (the transiting side). */
  asTransit: string;
  /** Noun phrase used when this body is the one being hit (the natal side). */
  asNatal: string;
  /** Short label for the natal side: "natal Venus", "your Rising sign". */
  natalLabel: string;
  /** Life area this body owns when nothing more specific applies. */
  area: LifeArea;
  term?: string;
}

export const PLANET_VOICES: Record<Planet, PlanetVoice> = {
  sun: {
    keyword: 'identity',
    asTransit: 'The Sun lights up whatever it touches',
    asNatal: 'your core sense of who you are',
    natalLabel: 'natal Sun',
    area: 'inner',
    term: 'sun-sign',
  },
  moon: {
    keyword: 'feelings',
    asTransit: 'The Moon is the fastest mood-setter in the chart',
    asNatal: 'how you feel and what actually soothes you',
    natalLabel: 'natal Moon',
    area: 'inner',
    term: 'moon-sign',
  },
  mercury: {
    keyword: 'thinking',
    asTransit: 'Mercury runs how things get thought and said',
    asNatal: 'how you think, talk and take in information',
    natalLabel: 'natal Mercury',
    area: 'learning',
  },
  venus: {
    keyword: 'closeness',
    asTransit: 'Venus pulls toward comfort, taste and connection',
    asNatal: 'what you find beautiful and who you let close',
    natalLabel: 'natal Venus',
    area: 'relationships',
  },
  mars: {
    keyword: 'drive',
    asTransit: 'Mars pushes you to act, argue or start something',
    asNatal: 'your drive — how you go after things and how you fight',
    natalLabel: 'natal Mars',
    area: 'health',
  },
  jupiter: {
    keyword: 'expansion',
    asTransit: 'Jupiter slowly widens whatever it lands on',
    asNatal: 'where you look for meaning, growth and a bigger picture',
    natalLabel: 'natal Jupiter',
    area: 'learning',
  },
  saturn: {
    keyword: 'structure',
    asTransit: 'Saturn asks, slowly, for structure, patience and proof',
    asNatal: 'where you take things seriously and where you doubt yourself',
    natalLabel: 'natal Saturn',
    area: 'career',
    term: 'saturn-return',
  },
  uranus: {
    keyword: 'disruption',
    asTransit: 'Uranus breaks a routine open',
    asNatal: 'the part of you that refuses to be predictable',
    natalLabel: 'natal Uranus',
    area: 'inner',
  },
  neptune: {
    keyword: 'blur',
    asTransit: 'Neptune softens edges and runs on dream logic',
    asNatal: 'your imagination, and the places you are easiest to fool',
    natalLabel: 'natal Neptune',
    area: 'inner',
  },
  pluto: {
    keyword: 'depth',
    asTransit: 'Pluto rewrites something at the root, very slowly',
    asNatal: 'what you are most intense about, and what you control',
    natalLabel: 'natal Pluto',
    area: 'inner',
  },
  rahu: {
    keyword: 'hunger',
    asTransit: 'Rahu, the north lunar node, is appetite for something new and unfamiliar',
    asNatal: 'the direction you are pulled toward but have not mastered',
    natalLabel: 'natal Rahu',
    area: 'career',
  },
  ketu: {
    keyword: 'release',
    asTransit: 'Ketu, the south lunar node, is the urge to drop what is already done',
    asNatal: 'what already comes easily, to the point of boredom',
    natalLabel: 'natal Ketu',
    area: 'inner',
  },
};

/** Sanskrit names for the grahas, used in the Vedic register. */
export const GRAHA_NAMES: Partial<Record<Planet, string>> = {
  sun: 'Surya',
  moon: 'Chandra',
  mars: 'Mangal',
  mercury: 'Budha',
  jupiter: 'Guru',
  venus: 'Shukra',
  saturn: 'Shani',
  rahu: 'Rahu',
  ketu: 'Ketu',
};

/**
 * "Shani", bare. This used to return "Shani (Saturn)" on the theory that a
 * Sanskrit name must never go unexplained — but the explanation lives in the
 * tappable glossary now (every signal carries term ids and the UI wraps
 * them), and the parenthetical English was DOUBLING the length of every
 * Jyotish label while quietly teaching the reader the Western name instead
 * of the Indian one. The founder's instruction is the product rule: Indian
 * terms in Indian surfaces; the Western equivalent lives one tap away.
 */
export function grahaLabel(planet: Planet): string {
  const sanskrit = GRAHA_NAMES[planet];
  const english = planet.charAt(0).toUpperCase() + planet.slice(1);
  return sanskrit ?? english;
}

/** Non-planet chart points that transits can hit. */
export const POINT_VOICES: Record<Exclude<ChartPointId, Planet>, PlanetVoice> = {
  ascendant: {
    keyword: 'presence',
    asTransit: 'The horizon of the chart marks where you meet the world',
    asNatal: 'the version of you people meet first',
    natalLabel: 'Rising sign',
    area: 'inner',
    term: 'rising-sign',
  },
  mc: {
    keyword: 'reputation',
    asTransit: 'The top of the chart is your public direction',
    asNatal: 'your public direction — what you are visibly building',
    natalLabel: 'Midheaven',
    area: 'career',
    term: 'midheaven',
  },
  fortune: {
    keyword: 'flow',
    asTransit: 'The Part of Fortune marks where things come together',
    asNatal: 'the spot classical astrology reads as where things come together easily',
    natalLabel: 'Part of Fortune',
    area: 'money',
    term: 'part-of-fortune',
  },
};

export function voiceFor(point: ChartPointId): PlanetVoice {
  return point in PLANET_VOICES
    ? PLANET_VOICES[point as Planet]
    : POINT_VOICES[point as Exclude<ChartPointId, Planet>];
}

// ---------------------------------------------------------------------------
// Aspect tones — what the ANGLE itself means
// ---------------------------------------------------------------------------

export interface AspectTone {
  /** Verb for the label: "Saturn <verb> your natal Venus". */
  verb: string;
  /** Adjective for the meaning: "in <word> contact with…". */
  word: string;
  /** The one-line reading of the angle. */
  meaning: string;
  /** How much this angle is worth, 0..1. */
  weight: number;
  term: string;
}

export const ASPECT_TONES: Record<WesternAspectType, AspectTone> = {
  conjunction: {
    verb: 'sits on',
    word: 'blended',
    meaning: 'conjunctions fuse — the two stop being separate and you get one loud signal',
    weight: 1,
    term: 'conjunction',
  },
  opposition: {
    verb: 'faces off with',
    word: 'see-saw',
    meaning: 'oppositions are a tug of war between two real needs; the work is balance, not picking a side',
    weight: 0.95,
    term: 'opposition',
  },
  square: {
    verb: 'squares',
    word: 'tense',
    meaning: 'squares grind: two parts of you want different things, and the friction is where the honesty is',
    weight: 0.9,
    term: 'square',
  },
  trine: {
    verb: 'trines',
    word: 'easy',
    meaning: 'trines flow — it works without effort, which is also why it is easy to waste',
    weight: 0.75,
    term: 'trine',
  },
  sextile: {
    verb: 'sextiles',
    word: 'open',
    meaning: 'sextiles are a door left ajar; nothing happens unless you walk through it',
    weight: 0.55,
    term: 'sextile',
  },
  quincunx: {
    verb: 'sits awkwardly to',
    word: 'mismatched',
    meaning: 'quincunxes do not speak the same language, so they ask for constant small adjustments',
    weight: 0.45,
    term: 'quincunx',
  },
  'semi-sextile': {
    verb: 'lightly nudges',
    word: 'faint',
    meaning: 'a background hum — mild enough that it mostly colours the mood',
    weight: 0.25,
    term: 'semisextile',
  },
  'semi-square': {
    verb: 'chafes against',
    word: 'itchy',
    meaning: 'low-grade irritation that usually points at something you have been avoiding',
    weight: 0.3,
    term: 'semisquare',
  },
  sesquiquadrate: {
    verb: 'pulls awkwardly at',
    word: 'restless',
    meaning: 'a restless edge: real pressure, but the undramatic kind that leaks out sideways',
    weight: 0.3,
    term: 'sesquiquadrate',
  },
};

/**
 * Bespoke copy for the configurations everyone has heard of. Keyed
 * `transiting:aspect:natal`. Anything not listed here is composed from the
 * planet voices + aspect tone above, which is why the table can stay short.
 */
export const ASPECT_OVERRIDES: Record<string, Theme> = {
  'saturn:conjunction:moon': {
    label: 'Saturn sitting on your Moon',
    meaning:
      'The slowest teacher parked on your feelings. A stretch where everything asks to be taken seriously and rest is not optional — heavy, not doomed.',
    area: 'inner',
    weight: 1,
    term: 'sade-sati',
  },
  'saturn:opposition:moon': {
    label: 'Saturn opposite your Moon',
    meaning:
      'What you need emotionally and what your responsibilities demand are sitting on opposite ends of a see-saw. Worth asking where you have been over-giving.',
    area: 'inner',
    weight: 0.95,
  },
  'saturn:square:sun': {
    label: 'Saturn squaring your Sun',
    meaning:
      'A pressure test on identity: the version of yourself you have been running gets asked for receipts. Uncomfortable, and usually clarifying.',
    area: 'career',
    weight: 0.95,
  },
  'saturn:conjunction:sun': {
    label: 'Saturn meeting your Sun',
    meaning:
      'A reset on how seriously you take yourself. Reads as a slow, unglamorous rebuild of what you are actually here to do.',
    area: 'career',
    weight: 0.95,
  },
  'saturn:conjunction:saturn': {
    label: 'Saturn return',
    meaning:
      'Saturn is back where it was at your birth — it takes about 29 years to lap the chart. Traditionally read as the "grow up on your own terms" chapter, not a punishment.',
    area: 'career',
    weight: 1,
    term: 'saturn-return',
  },
  'saturn:conjunction:ascendant': {
    label: 'Saturn crossing your Rising sign',
    meaning:
      'The way you show up gets stripped back to essentials. This is about upkeep of who you are — not bad luck.',
    area: 'inner',
    weight: 0.9,
    term: 'rising-sign',
  },
  'saturn:conjunction:mc': {
    label: 'Saturn at the top of your chart',
    meaning:
      'Your public direction is under review. Read it as the difference between what you say you are building and what you are actually building.',
    area: 'career',
    weight: 0.9,
    term: 'midheaven',
  },
  'jupiter:conjunction:jupiter': {
    label: 'Jupiter return',
    meaning:
      'Jupiter is back where it started, roughly every 12 years. Read as a new chapter of the "what do I want more of" question.',
    area: 'learning',
    weight: 0.9,
  },
  'jupiter:conjunction:sun': {
    label: 'Jupiter meeting your Sun',
    meaning:
      'The expansive one lands on your sense of self. Confidence and appetite both grow — including the risk of over-promising.',
    area: 'inner',
    weight: 0.85,
  },
  'jupiter:trine:venus': {
    label: 'Jupiter flowing to your Venus',
    meaning:
      'Ease around people, taste and money. Genuinely pleasant, and easy to sleep through — flowing aspects only pay out if you use them.',
    area: 'relationships',
    weight: 0.7,
  },
  'jupiter:conjunction:mc': {
    label: 'Jupiter at the top of your chart',
    meaning:
      'A widening of what you are publicly known for. Read as more room, not guaranteed success.',
    area: 'career',
    weight: 0.85,
    term: 'midheaven',
  },
  'sun:conjunction:sun': {
    label: 'Solar return — your astrological birthday',
    meaning:
      'The Sun is back at its birth degree. The chart for this moment is the traditional starting frame for your year ahead.',
    area: 'inner',
    weight: 0.8,
    term: 'solar-return',
  },
  'moon:conjunction:moon': {
    label: 'Lunar return',
    meaning:
      'The Moon is back on its birth spot, which happens about once a month. Read as an emotional reset point, not an event.',
    area: 'inner',
    weight: 0.55,
  },
  'mars:conjunction:mars': {
    label: 'Mars return',
    meaning:
      'Your drive restarts its roughly two-year cycle. Time to pick what you want to put energy behind next.',
    area: 'health',
    weight: 0.7,
  },
  'pluto:square:sun': {
    label: 'Pluto squaring your Sun',
    meaning:
      'A years-long question about power and control in your own life. Slow, structural, and not something a single day explains.',
    area: 'inner',
    weight: 1,
  },
  'uranus:opposition:sun': {
    label: 'Uranus opposite your Sun',
    meaning:
      'The unpredictable one faces your sense of self. The long question: how much of your routine is actually yours?',
    area: 'inner',
    weight: 0.95,
  },
  'neptune:square:sun': {
    label: 'Neptune squaring your Sun',
    meaning:
      'Edges get soft. Watch where you are idealising something — including yourself.',
    area: 'inner',
    weight: 0.9,
  },
};

/**
 * How much a transit to this natal point is worth.
 *
 * Everyone born within a few years of you shares a natal Uranus, Neptune and
 * Pluto degree, so a transit to one of those is a generational backdrop, not a
 * reading about you. Without this, slow-to-slow contacts (which score highest
 * on speed alone) would headline every single day.
 */
export const NATAL_POINT_WEIGHTS: Record<ChartPointId, number> = {
  sun: 1,
  moon: 1,
  ascendant: 1,
  mc: 0.95,
  mercury: 0.9,
  venus: 0.9,
  mars: 0.9,
  jupiter: 0.8,
  saturn: 0.8,
  fortune: 0.7,
  rahu: 0.6,
  ketu: 0.6,
  uranus: 0.35,
  neptune: 0.35,
  pluto: 0.35,
};

export function aspectOverrideKey(
  transiting: Planet,
  type: WesternAspectType,
  natal: ChartPointId,
): string {
  return `${transiting}:${type}:${natal}`;
}

/**
 * The (transiting planet x natal point x aspect type) theme.
 *
 * Composed from data rather than enumerated: 12 x 15 x 9 is 1620 combinations,
 * and hand-writing them would guarantee drift. The named classics live in
 * ASPECT_OVERRIDES and win.
 */
export function aspectTheme(
  transiting: Planet,
  type: WesternAspectType,
  natal: ChartPointId,
): Theme {
  const override = ASPECT_OVERRIDES[aspectOverrideKey(transiting, type, natal)];
  if (override) return override;

  const mover = PLANET_VOICES[transiting];
  const target = voiceFor(natal);
  const tone = ASPECT_TONES[type];
  const moverName = transiting.charAt(0).toUpperCase() + transiting.slice(1);

  return {
    label: `${moverName} ${tone.verb} your ${target.natalLabel}`,
    meaning: `${mover.asTransit}, and right now it is in ${tone.word} contact with ${target.asNatal}. ${tone.meaning.charAt(0).toUpperCase()}${tone.meaning.slice(1)}.`,
    area: target.area,
    weight: tone.weight,
    term: tone.term,
  };
}

// ---------------------------------------------------------------------------
// Gochara — houses counted from the natal Moon (the Vedic reference point)
// ---------------------------------------------------------------------------

/**
 * In Jyotish a transit ("gochara") is read from the sign the Moon occupied at
 * birth — the chandra lagna — not from the Sun sign. These twelve entries are
 * the life-areas that get lit as a graha moves through each house from it.
 *
 * `label` is a FRAGMENT: it is composed after the graha's name, so it reads
 * "Shani (Saturn) in your 8th bhava from Chandra — the hidden stuff".
 */
export const MOON_HOUSE_THEMES: Record<number, Theme> = {
  1: {
    label: 'on your Chandra rashi — mood and body',
    meaning:
      'Sitting right on your emotional home base — whatever this planet is about, you feel it directly rather than watching it happen.',
    area: 'inner',
    weight: 0.9,
    term: 'moon-sign',
  },
  2: {
    label: 'in your 2nd bhava from Chandra — money, family, what you say',
    meaning:
      'The house of resources, food, close family and speech. About what you are accumulating, and how you are talking about it.',
    area: 'money',
    weight: 0.7,
    term: 'bhava',
  },
  3: {
    label: 'in your 3rd bhava from Chandra — nerve and effort',
    meaning:
      'Courage, siblings, short trips, the daily grind of trying. Classically one of the easier places for a difficult planet to sit.',
    area: 'learning',
    weight: 0.6,
    term: 'bhava',
  },
  4: {
    label: 'in your 4th bhava from Chandra — home and peace of mind',
    meaning:
      'House of home, mother, property and inner calm. Read as where your sense of "safe" is being touched.',
    area: 'home',
    weight: 0.75,
    term: 'bhava',
  },
  5: {
    label: 'in your 5th bhava from Chandra — creativity and romance',
    meaning:
      'Creativity, study, romance, children, the stuff you do because you want to. This one is about play, not obligation.',
    area: 'relationships',
    weight: 0.65,
    term: 'bhava',
  },
  6: {
    label: 'in your 6th bhava from Chandra — work, health, friction',
    meaning:
      'The house of daily work, health routines, debts and rivals. Hard planets are traditionally read as doing well here — you fight better than you rest.',
    area: 'health',
    weight: 0.7,
    term: 'bhava',
  },
  7: {
    label: 'in your 7th bhava from Chandra — the other person',
    meaning:
      'Partnership, marriage, deals, opponents. How the one-on-one part of your life is sitting right now.',
    area: 'relationships',
    weight: 0.8,
    term: 'bhava',
  },
  8: {
    label: 'in your 8th bhava from Chandra — the hidden stuff',
    meaning:
      'Shared money, secrets, endings and things that change you. Classically the least comfortable house; read as depth, not disaster.',
    area: 'inner',
    weight: 0.85,
    term: 'bhava',
  },
  9: {
    label: 'in your 9th bhava from Chandra — belief and distance',
    meaning:
      'Teachers, long journeys, higher study and what you believe. About your bigger frame of reference.',
    area: 'travel',
    weight: 0.7,
    term: 'bhava',
  },
  10: {
    label: 'in your 10th bhava from Chandra — the work you are known for',
    meaning:
      'Career, status and public action. The visible half of your life.',
    area: 'career',
    weight: 0.8,
    term: 'bhava',
  },
  11: {
    label: 'in your 11th bhava from Chandra — gains and networks',
    meaning:
      'Friends, groups, income and what you get out of things. Traditionally the friendliest house for any planet.',
    area: 'money',
    weight: 0.7,
    term: 'bhava',
  },
  12: {
    label: 'in your 12th bhava from Chandra — rest, retreat, letting go',
    meaning:
      'Sleep, expenses, foreign places, endings and solitude. Where energy is quietly draining out — sometimes usefully.',
    area: 'inner',
    weight: 0.75,
    term: 'bhava',
  },
};

/**
 * How much each graha's gochara is worth. Saturn and Jupiter carry a Vedic
 * transit reading; the fast movers colour a day and little more.
 */
export const GOCHARA_WEIGHTS: Partial<Record<Planet, number>> = {
  saturn: 1,
  jupiter: 0.92,
  rahu: 0.78,
  ketu: 0.78,
  mars: 0.55,
  sun: 0.45,
  venus: 0.4,
  mercury: 0.38,
  moon: 0.32,
};

// ---------------------------------------------------------------------------
// Sade Sati
// ---------------------------------------------------------------------------

/** Saturn transiting the 12th, 1st or 2nd from the natal Moon — about 7.5 years. */
export const SADE_SATI_PHASES: Record<number, Theme> = {
  12: {
    label: 'Sade Sati — first phase (Saturn in your 12th bhava from Chandra)',
    meaning:
      'Sade Sati is the roughly seven-and-a-half years Saturn spends crossing the three signs around your birth Moon. The opening phase is read as the tiring one: energy and money leak out and rest stops being optional.',
    area: 'inner',
    weight: 0.92,
    term: 'sade-sati',
  },
  1: {
    label: 'Sade Sati — peak phase (Saturn on your Moon sign)',
    meaning:
      'Sade Sati is the roughly seven-and-a-half years Saturn spends crossing the three signs around your birth Moon. This is the middle stretch, with Saturn sitting on the Moon sign itself — traditionally the most demanding part, where everything asks to be taken seriously. It is about responsibility — not a prediction of misfortune.',
    area: 'inner',
    weight: 1,
    term: 'sade-sati',
  },
  2: {
    label: 'Sade Sati — final phase (Saturn in your 2nd bhava from Chandra)',
    meaning:
      'Sade Sati is the roughly seven-and-a-half years Saturn spends crossing the three signs around your birth Moon. This is the closing stretch, touching money, family and speech — read as consolidation, where what survived the last few years turns out to be what was actually yours.',
    area: 'money',
    weight: 0.88,
    term: 'sade-sati',
  },
};

export const SADE_SATI_PHASE_NAMES: Record<number, 'rising' | 'peak' | 'setting'> = {
  12: 'rising',
  1: 'peak',
  2: 'setting',
};

// ---------------------------------------------------------------------------
// Dashas — the Vimshottari period lords
// ---------------------------------------------------------------------------

export type DashaLord =
  | 'ketu'
  | 'venus'
  | 'sun'
  | 'moon'
  | 'mars'
  | 'rahu'
  | 'jupiter'
  | 'saturn'
  | 'mercury';

const DASHA_LORD_SET: ReadonlySet<string> = new Set<string>([
  'ketu',
  'venus',
  'sun',
  'moon',
  'mars',
  'rahu',
  'jupiter',
  'saturn',
  'mercury',
]);

export function isDashaLord(planet: Planet): planet is DashaLord {
  return DASHA_LORD_SET.has(planet);
}

/**
 * The mahadasha/antardasha lord sets the background colour of a whole chapter
 * of life. It does not change day to day — which is exactly why it belongs in
 * every reading: it is the context the daily stuff sits inside.
 *
 * `meaning` deliberately omits the period LENGTH. Those numbers live in
 * DASHA_SEQUENCE in jyotish/vimshottari.ts and are read from there, so the copy
 * cannot drift out of sync with the maths.
 */
export const DASHA_LORD_THEMES: Record<DashaLord, Theme> = {
  sun: {
    label: 'Sun period — visibility and self-definition',
    meaning:
      'A Sun chapter is read as being about authority, recognition and getting clear on what you actually stand for.',
    area: 'career',
    weight: 0.6,
    term: 'dasha',
  },
  moon: {
    label: 'Moon period — feeling, care and change',
    meaning:
      'A Moon chapter is read as emotionally busy: home, family, mood and the people who look after you.',
    area: 'home',
    weight: 0.6,
    term: 'dasha',
  },
  mars: {
    label: 'Mars period — push, conflict and building',
    meaning:
      'A Mars chapter reads as high-effort: property, siblings, competition, and a shorter fuse than usual.',
    area: 'health',
    weight: 0.62,
    term: 'dasha',
  },
  mercury: {
    label: 'Mercury period — talk, trade and learning',
    meaning:
      'A Mercury chapter reads as mentally fast: study, communication, business, negotiation.',
    area: 'learning',
    weight: 0.6,
    term: 'dasha',
  },
  jupiter: {
    label: 'Jupiter period — growth, meaning and teachers',
    meaning:
      'A Jupiter chapter reads as expansive: study, mentors, belief, and more room than before.',
    area: 'learning',
    weight: 0.65,
    term: 'dasha',
  },
  venus: {
    label: 'Venus period — pleasure, partnership and taste',
    meaning:
      'A Venus chapter reads as relational and comfort-seeking: art, money, love, the good life.',
    area: 'relationships',
    weight: 0.62,
    term: 'dasha',
  },
  saturn: {
    label: 'Saturn period — the long build',
    meaning:
      'A Saturn chapter reads as slow and demanding: work, discipline, delay, and things that only pay off if you stay.',
    area: 'career',
    weight: 0.7,
    term: 'dasha',
  },
  rahu: {
    label: 'Rahu period — appetite for the unfamiliar',
    meaning:
      'A Rahu chapter reads as ambitious and disorienting: new worlds, foreign things, wanting more without a map.',
    area: 'career',
    weight: 0.68,
    term: 'dasha',
  },
  ketu: {
    label: 'Ketu period — stripping back',
    meaning:
      'A Ketu chapter reads as detachment: losing interest in things you used to chase, and going inward.',
    area: 'inner',
    weight: 0.64,
    term: 'dasha',
  },
};

/** The antardasha (sub-period) is the nearer-term flavour inside the mahadasha. */
export const DASHA_LEVEL_FRAMES: Record<1 | 2 | 3, { name: string; term: string; weight: number }> = {
  1: { name: 'mahadasha', term: 'mahadasha', weight: 1 },
  2: { name: 'antardasha', term: 'antardasha', weight: 0.9 },
  3: { name: 'pratyantardasha', term: 'dasha', weight: 0.5 },
};

// ---------------------------------------------------------------------------
// Panchanga — the five limbs of the Vedic day
// ---------------------------------------------------------------------------

/** Weekday index 0 = Sunday. The vara is the first limb of the panchanga. */
export const VARA_THEMES: Record<number, Theme> = {
  0: {
    label: 'Ravivara (Sunday) — ruled by Surya, the Sun',
    meaning: 'Sun day. Read as a day for your own name on things: authority, visibility, doing it yourself.',
    area: 'career',
    weight: 0.3,
    term: 'vaara',
  },
  1: {
    label: 'Somavara (Monday) — ruled by Chandra, the Moon',
    meaning: 'Moon day. Read as a day that runs on mood and care rather than push.',
    area: 'inner',
    weight: 0.3,
    term: 'vaara',
  },
  2: {
    label: 'Mangalavara (Tuesday) — ruled by Mangal, Mars',
    meaning: 'Mars day. Read as a day with an edge on it: physical, direct, and quicker to argue.',
    area: 'health',
    weight: 0.3,
    term: 'vaara',
  },
  3: {
    label: 'Budhavara (Wednesday) — ruled by Budha, Mercury',
    meaning: 'Mercury day. Read as a day for messages, paperwork, study and anything that needs words.',
    area: 'learning',
    weight: 0.3,
    term: 'vaara',
  },
  4: {
    label: 'Guruvara (Thursday) — ruled by Guru, Jupiter',
    meaning: 'Jupiter day. Traditionally the most favoured weekday: teachers, advice, and the bigger picture.',
    area: 'learning',
    weight: 0.3,
    term: 'vaara',
  },
  5: {
    label: 'Shukravara (Friday) — ruled by Shukra, Venus',
    meaning: 'Venus day. Read as a day for people, beauty, comfort and things you actually enjoy.',
    area: 'relationships',
    weight: 0.3,
    term: 'vaara',
  },
  6: {
    label: 'Shanivara (Saturday) — ruled by Shani, Saturn',
    meaning: 'Saturn day. Read as a day for the unglamorous work — the stuff that only clears if you sit with it.',
    area: 'career',
    weight: 0.3,
    term: 'vaara',
  },
};

/** Tithi groups repeat every five lunar days and set the tone of the tithi. */
export type TithiGroup = 'nanda' | 'bhadra' | 'jaya' | 'rikta' | 'purna';

export const TITHI_GROUP_ORDER: TithiGroup[] = ['nanda', 'bhadra', 'jaya', 'rikta', 'purna'];

export const TITHI_GROUP_THEMES: Record<TithiGroup, Theme> = {
  nanda: {
    label: 'Nanda tithi — a starting kind of day',
    meaning:
      'Tithi is the Moon–Sun distance, the lunar "day". Nanda tithis (1st, 6th, 11th) are traditionally read as light and good for beginning things.',
    area: 'inner',
    weight: 0.3,
    term: 'tithi',
  },
  bhadra: {
    label: 'Bhadra tithi — a steady kind of day',
    meaning:
      'Bhadra tithis (2nd, 7th, 12th) are read as solid and domestic — the tone for continuing something rather than launching it.',
    area: 'home',
    weight: 0.3,
    term: 'tithi',
  },
  jaya: {
    label: 'Jaya tithi — a push-through kind of day',
    meaning:
      'Jaya tithis (3rd, 8th, 13th) carry a competitive tone; traditionally read as favouring effort that meets resistance.',
    area: 'career',
    weight: 0.32,
    term: 'tithi',
  },
  rikta: {
    label: 'Rikta tithi — a clearing-out kind of day',
    meaning:
      'Rikta means "empty". The 4th, 9th and 14th are traditionally avoided for new starts and read as better for finishing, removing and repairing.',
    area: 'health',
    weight: 0.34,
    term: 'tithi',
  },
  purna: {
    label: 'Purna tithi — a completing kind of day',
    meaning:
      'Purna means "full". The 5th, 10th and 15th are read as the fullest tithis — the tone for wrapping something up.',
    area: 'money',
    weight: 0.32,
    term: 'tithi',
  },
};

/** The two tithis people actually feel. */
export const PURNIMA_THEME: Theme = {
  label: 'Purnima — full Moon',
  meaning:
    'The Moon is exactly opposite the Sun. The classic full-Moon reading: everything is visible, including the things you had been managing not to look at.',
  area: 'inner',
  weight: 0.5,
  term: 'tithi',
};

export const AMAVASYA_THEME: Theme = {
  label: 'Amavasya — new Moon',
  meaning:
    'The Moon and Sun share a degree, so there is no Moon in the sky. Traditionally a low-energy, inward day — read as a reset point rather than a launch pad.',
  area: 'inner',
  weight: 0.5,
  term: 'tithi',
};

/** Nakshatra of the Moon — the limb of the panchanga that changes the day's flavour most. */
export const NAKSHATRA_THEMES: Record<number, Theme> = {
  0: { label: 'Moon in Ashwini — quick starts', meaning: 'Ashwini is sprint energy: fast openings, first moves, patch-it-up fixes. Good for beginning, less for finishing.', area: 'health', weight: 0.55 },
  1: { label: 'Moon in Bharani — carrying weight', meaning: 'Bharani is about what you are willing to bear: limits, endings and pressure that shapes rather than breaks.', area: 'inner', weight: 0.55 },
  2: { label: 'Moon in Krittika — the clean cut', meaning: 'Krittika is the sharp edge that separates real from filler. Honest, occasionally blunt.', area: 'career', weight: 0.55 },
  3: { label: 'Moon in Rohini — grow something', meaning: 'Rohini is the fertile, sensory one: comfort, beauty and building something that lasts.', area: 'money', weight: 0.55 },
  4: { label: 'Moon in Mrigashira — search mode', meaning: 'Mrigashira is curiosity on legs — looking, sampling, deliberately not settling yet.', area: 'learning', weight: 0.55 },
  5: { label: 'Moon in Ardra — storm then clear', meaning: 'Ardra is the emotional weather that breaks tension. Uncomfortable first, clearer after.', area: 'inner', weight: 0.55 },
  6: { label: 'Moon in Punarvasu — the return', meaning: 'Punarvasu is the reset: coming back to yourself after a detour, with nothing actually lost.', area: 'home', weight: 0.55 },
  7: { label: 'Moon in Pushya — nourish it', meaning: 'Pushya is the caretaking one — steady, feeding, and classically the safest nakshatra of all.', area: 'home', weight: 0.55 },
  8: { label: 'Moon in Ashlesha — read the room', meaning: 'Ashlesha is instinct and undercurrent: it notices exactly what nobody said out loud.', area: 'inner', weight: 0.55 },
  9: { label: 'Moon in Magha — where you come from', meaning: 'Magha is lineage: ancestors, inherited status, and the seat you did not choose.', area: 'home', weight: 0.55 },
  10: { label: 'Moon in Purva Phalguni — allowed to enjoy it', meaning: 'Purva Phalguni is pleasure, rest and romance — the part of life you are permitted to like.', area: 'relationships', weight: 0.55 },
  11: { label: 'Moon in Uttara Phalguni — keep the promise', meaning: 'Uttara Phalguni is the contract half of pleasure: friendship, loyalty, what you actually commit to.', area: 'relationships', weight: 0.55 },
  12: { label: 'Moon in Hasta — make it with your hands', meaning: 'Hasta is skill and craft — the detail work that turns an idea into an actual thing.', area: 'career', weight: 0.55 },
  13: { label: 'Moon in Chitra — an eye for form', meaning: 'Chitra is design sense: aesthetics, structure, making something look and sit right.', area: 'career', weight: 0.55 },
  14: { label: 'Moon in Swati — move freely', meaning: 'Swati is wind: independence, trade, movement, and a strong dislike of being tied down.', area: 'travel', weight: 0.55 },
  15: { label: 'Moon in Vishakha — chase the goal', meaning: 'Vishakha is focused wanting — the drive that does not let go until the thing lands.', area: 'career', weight: 0.55 },
  16: { label: 'Moon in Anuradha — show up for people', meaning: 'Anuradha is devotion and friendship that survives distance and inconvenience.', area: 'relationships', weight: 0.55 },
  17: { label: 'Moon in Jyeshtha — carry the weight', meaning: 'Jyeshtha is seniority: responsibility, protectiveness, and the isolation that comes with both.', area: 'career', weight: 0.55 },
  18: { label: 'Moon in Mula — pull the root', meaning: 'Mula digs to the cause. It is the one that dismantles before it rebuilds.', area: 'inner', weight: 0.55 },
  19: { label: 'Moon in Purva Ashadha — believe loudly', meaning: 'Purva Ashadha is unbeatable-feeling optimism: persuasion, momentum, and no interest in the downside.', area: 'learning', weight: 0.55 },
  20: { label: 'Moon in Uttara Ashadha — win slowly', meaning: 'Uttara Ashadha is the durable victory — earned rather than lucky, and therefore slow.', area: 'career', weight: 0.55 },
  21: { label: 'Moon in Shravana — listen', meaning: 'Shravana learns by hearing: teaching, advice, and the conversation that changes your mind.', area: 'learning', weight: 0.55 },
  22: { label: 'Moon in Dhanishta — keep the rhythm', meaning: 'Dhanishta is tempo and group energy: music, teamwork, and wealth built while moving.', area: 'money', weight: 0.55 },
  23: { label: 'Moon in Shatabhisha — heal in private', meaning: 'Shatabhisha is the outsider healer: research, secrecy, and fixing what everyone else ignored.', area: 'health', weight: 0.55 },
  24: { label: 'Moon in Purva Bhadrapada — the edge', meaning: 'Purva Bhadrapada is intensity: drawn to extremes, hidden depths and things other people avoid.', area: 'inner', weight: 0.55 },
  25: { label: 'Moon in Uttara Bhadrapada — go quiet', meaning: 'Uttara Bhadrapada is deep calm — the still, patient version of intensity.', area: 'inner', weight: 0.55 },
  26: { label: 'Moon in Revati — finish and let go', meaning: 'Revati is the safe ending: closure, kindness, and seeing someone off properly.', area: 'travel', weight: 0.55 },
};

/**
 * Indices read as rough-edged in a general reading.
 *
 * Broader than `INAUSPICIOUS_YOGA_INDICES` in jyotish/panchanga.ts, which keeps
 * the strict pair (Vyatipata, Vaidhriti) that muhurta selection rules out
 * outright. This is a tone, not a prohibition.
 */
const TENSE_NITYA_YOGAS: ReadonlySet<number> = new Set([0, 5, 8, 9, 12, 14, 16, 18, 26]);

export function nityaYogaTheme(index: number): Theme {
  const i = ((index % 27) + 27) % 27;
  const name = YOGA_NAMES[i];
  const tense = TENSE_NITYA_YOGAS.has(i);
  return {
    label: `${name} yoga`,
    meaning: tense
      ? `The nitya yoga (the Sun and Moon added together, split into 27) is ${name} — one of the nine traditionally read as rough-edged. Treated as a "go slower, check twice" tone for the day, nothing more.`
      : `The nitya yoga (the Sun and Moon added together, split into 27) is ${name} — read as a supportive background tone for the day.`,
    area: 'inner',
    weight: tense ? 0.28 : 0.22,
    term: 'panchanga',
  };
}

/** Karana — half a tithi, the fifth and shortest limb. Names come from panchanga.ts. */
export function karanaTheme(name: string): Theme {
  const vishti = name === CHARA_KARANA_NAMES[6];
  return {
    label: `${name} karana`,
    meaning: vishti
      ? 'A karana is half a tithi. Vishti (also called Bhadra) is the one traditional almanacs mark as unfavourable for starting things — a "finish, do not launch" tone.'
      : `A karana is half a tithi — the shortest limb of the panchanga. This one is ${name}, read as an ordinary, workable tone.`,
    area: 'inner',
    weight: vishti ? 0.26 : 0.15,
    term: 'panchanga',
  };
}

// ---------------------------------------------------------------------------
// Western lunar phase — the day-level driver on that side
// ---------------------------------------------------------------------------

export type MoonPhase =
  | 'new'
  | 'waxing-crescent'
  | 'first-quarter'
  | 'waxing-gibbous'
  | 'full'
  | 'waning-gibbous'
  | 'last-quarter'
  | 'balsamic';

export const MOON_PHASE_ORDER: MoonPhase[] = [
  'new',
  'waxing-crescent',
  'first-quarter',
  'waxing-gibbous',
  'full',
  'waning-gibbous',
  'last-quarter',
  'balsamic',
];

export const MOON_PHASE_THEMES: Record<MoonPhase, Theme> = {
  new: {
    label: 'New Moon',
    meaning: 'Sun and Moon in the same degree. The dark part of the cycle — read as a starting point you cannot see the end of yet.',
    area: 'inner',
    weight: 0.5,
  },
  'waxing-crescent': {
    label: 'Waxing crescent Moon',
    meaning: 'Light building. Read as the awkward early stretch where a new thing is real but not yet convincing.',
    area: 'inner',
    weight: 0.35,
  },
  'first-quarter': {
    label: 'First quarter Moon',
    meaning: 'Moon square Sun. The classic friction point of the cycle — read as where a plan meets its first actual obstacle.',
    area: 'inner',
    weight: 0.45,
  },
  'waxing-gibbous': {
    label: 'Waxing gibbous Moon',
    meaning: 'Nearly full. Read as the refining stretch: adjusting rather than starting.',
    area: 'inner',
    weight: 0.35,
  },
  full: {
    label: 'Full Moon',
    meaning: 'Moon opposite Sun. Everything is lit, including what you had been managing not to look at. Read as visibility, not drama.',
    area: 'inner',
    weight: 0.5,
  },
  'waning-gibbous': {
    label: 'Waning gibbous Moon',
    meaning: 'Light receding. Traditionally the sharing part of the cycle — read as making sense of something out loud.',
    area: 'inner',
    weight: 0.35,
  },
  'last-quarter': {
    label: 'Last quarter Moon',
    meaning: 'Moon square Sun again, on the way down. Read as the point where you decide what is not coming with you.',
    area: 'inner',
    weight: 0.45,
  },
  balsamic: {
    label: 'Balsamic (dark) Moon',
    meaning: 'The last sliver before new. Read as low-fuel, closing-down time — rest is the assignment.',
    area: 'inner',
    weight: 0.4,
  },
};

// ---------------------------------------------------------------------------
// Period framing & weighting
// ---------------------------------------------------------------------------

export type HoroscopePeriod = 'day' | 'week' | 'month' | 'year';

export const PERIOD_FRAMES: Record<HoroscopePeriod, { word: string; scope: string }> = {
  day: {
    word: 'Today',
    scope: 'A day reading tracks the fast movers — mostly the Moon — against your chart.',
  },
  week: {
    word: 'This week',
    scope: 'A week reading is the seven days stacked together, so only what repeats or lands hard survives.',
  },
  month: {
    word: 'This month',
    scope: 'A month reading drops the Moon entirely and reads the medium-speed planets.',
  },
  year: {
    word: 'This year',
    scope: 'A year reading is only the slow arcs — Jupiter outward — plus the period you are in.',
  },
};

/**
 * Per-period multipliers on the transiting body. Missing = 1. Zero excludes
 * the body from that period entirely.
 *
 * Western weighting starts from the transiting planet's actual speed, so
 * without a boost the Moon would never surface. These bring the day reading
 * back to being Moon-driven, and take the Moon out of month and year readings
 * where it is pure noise.
 */
export const WESTERN_PERIOD_BOOST: Record<HoroscopePeriod, Partial<Record<Planet, number>>> = {
  day: { moon: 12, mercury: 1.7, venus: 1.7, sun: 1.7, mars: 1.35 },
  week: { moon: 4.5, mercury: 1.3, venus: 1.3, sun: 1.3, mars: 1.2 },
  month: { moon: 0, mercury: 0.8, venus: 0.9, sun: 1, mars: 1.1 },
  year: { moon: 0, mercury: 0, venus: 0, sun: 0.35, mars: 0.5 },
};

/**
 * Vedic gochara already starts from fixed per-graha weights that put Saturn and
 * Jupiter on top, so the Moon needs a much smaller nudge here than on the
 * Western side.
 */
export const VEDIC_PERIOD_BOOST: Record<HoroscopePeriod, Partial<Record<Planet, number>>> = {
  day: { moon: 2.2, mercury: 1.4, venus: 1.4, sun: 1.4, mars: 1.2 },
  week: { moon: 1.2, mercury: 1.1, venus: 1.1, sun: 1.1, mars: 1.1 },
  month: { moon: 0, mercury: 0.7, venus: 0.8, sun: 0.9, mars: 1 },
  year: { moon: 0, mercury: 0, venus: 0, sun: 0.3, mars: 0.5 },
};

/**
 * Retention thresholds, so a month reading is not forty Moon aspects long.
 *
 * `maxSignals` is deliberately larger for a day than for a week: a week is the
 * merge of its days, and a signal it keeps must still be findable in the day
 * reading it came from. `maxStrongest` is the UI surface; `byArea` carries the
 * rest.
 */
/**
 * The panchanga and the lunar phase are day-level limbs. A week reading keeps
 * them (that is what makes it an aggregate of its days) but at a discount, so
 * seven days of tithis cannot crowd out the dasha and the slow transits.
 * Scaling DOWN is deliberate: a week signal must never outweigh the same signal
 * inside the day it came from.
 */
export const PER_DAY_LIMB_SCALE: Record<HoroscopePeriod, number> = {
  day: 1,
  week: 0.8,
  month: 0,
  year: 0,
};

export const PERIOD_TUNING: Record<
  HoroscopePeriod,
  { minWeight: number; maxSignals: number; maxStrongest: number }
> = {
  day: { minWeight: 0.12, maxSignals: 20, maxStrongest: 5 },
  week: { minWeight: 0.2, maxSignals: 12, maxStrongest: 5 },
  month: { minWeight: 0.28, maxSignals: 12, maxStrongest: 5 },
  year: { minWeight: 0.34, maxSignals: 12, maxStrongest: 5 },
};

/**
 * Every narration of a horoscope must carry this. Astrology is an interpretive
 * reading here, never a claim about what will happen.
 */
export const HOROSCOPE_DISCLAIMER =
  'Astrology here is a way of reading meaning, not a forecast. It describes what these positions MEAN in the tradition they come from — it does not claim to know what will happen.';
